<div
    *ngIf="{
        venue: venue$ | async,
        event: event$ | async,
        mapUrl: mapUrl$ | async,
        directionsUrl: directionsUrl$ | async,
        slotGroups: eventBookingSlotGroups$ | async,
        modalIdealTime: modalIdealTime$ | async,
        activeDateTime: activeDateTime$ | async,
        eventDateOptions: eventDateOptions$ | async
    } as data"
    class="event-booking-page"
    appVenueColour
    [primaryColour]="data.venue?.primaryColour ?? null"
>
    @if (missingEventId) {
        <div class="container py-5 text-center">
            <h4 class="fw-semibold">Event not found</h4>
            <p class="text-secondary">The event you are looking for could not be loaded. Please check the link and try again.</p>
        </div>
    }
    @if (venueNotInService && !missingEventId) {
        <div class="container py-5">
            <app-venue-not-in-service></app-venue-not-in-service>
        </div>
    }
    @if (!venueNotInService && !data.venue && !missingEventId) {
        <div class="hstack justify-content-center p-5">
            <div class="spinner-border" role="status"></div>
        </div>
    }
    @if (!venueNotInService && data.venue && data.event) {
        <div class="container py-3 py-lg-4">
            <!-- Event name and venue name -->
            <div class="mb-3">
                <h2 class="fw-semibold m-0 text-secondary">{{ data.venue.displayName }}</h2>
                <h3 class="fw-semibold m-0">{{ data.event.displayName }}</h3>
            </div>

            <div class="row g-3 g-lg-4">
                <!-- Left column: description, images, booking card -->
                <div class="col-12 col-lg-7">
                    <div class="vstack gap-3">
                        @if (richTextPlainText(data.event.description)) {
                            <div class="quill-flush">
                                <quill-view class="d-block" id="event-page-description" data-testid="event-page-description" format="html" theme="bubble" [sanitize]="true" [content]="
                                    descriptionExpanded
                                        ? data.event.description
                                        : truncateRichTextHtml(data.event.description, descriptionCharacterLimit)
                                "></quill-view>
                                @if (richTextPlainText(data.event.description).length > descriptionCharacterLimit) {
                                    <button
                                        class="btn btn-link btn-sm p-0 mt-1 text-start"
                                        type="button"
                                        [attr.aria-expanded]="descriptionExpanded"
                                        aria-controls="event-page-description"
                                        (click)="descriptionExpanded = !descriptionExpanded"
                                    >
                                        {{ descriptionExpanded ? 'Read less' : 'Read more' }}
                                    </button>
                                }
                            </div>
                        }

                        @if (data.event.imageId) {
                            <img
                                [ngSrc]="environment.cdnUrl | thumbor : 'event-images/' + data.event.imageId : { width: 350, height: 300, aspectFit: true }"
                                class="rounded-3 object-fit-cover event-image"
                                width="350"
                                height="300"
                                priority
                            />
                        }

                        <div class="card pt-2 border-2 shadow-sm">
                            <app-booking-modal
                                [organisationId]="organisationId"
                                [businessId]="businessId"
                                [venueId]="venueId"
                                [eventId]="eventId"
                                [date]="data.activeDateTime?.toISOString() ?? ''"
                                [idealTime]="(data.modalIdealTime ?? data.activeDateTime)?.toISOString() ?? ''"
                                [embedded]="true"
                                [hideCloseButton]="true"
                            >
                            </app-booking-modal>
                        </div>
                    </div>
                </div>

                <!-- Right column: event details, location -->
                <div class="col-12 col-lg-5">
                    <div class="vstack gap-3 event-sidebar">
                        @if (data.event.depositAmount || data.event.depositFlatFeeAmount) {
                            <div class="card">
                                <div class="card-body hstack gap-3 align-items-start">
                                    <i class="bi bi-tag fs-5 text-dark"></i>
                                    <div class="vstack gap-1">
                                        <span class="text-secondary">Pricing</span>
                                        @if (data.event.depositAmount) {
                                            <span>
                                                {{ null | currencyUnit : data.event.depositAmount : data.venue.currencyCode }}
                                                per person
                                            </span>
                                        }
                                        @if (data.event.depositFlatFeeAmount) {
                                            <span>
                                                {{ null | currencyUnit : data.event.depositFlatFeeAmount : data.venue.currencyCode }}
                                                per booking
                                            </span>
                                        }
                                    </div>
                                </div>
                            </div>
                        }

                        @if (data.event.cancellationChargeAmount || data.event.cancellationChargePartyAmount) {
                            <div class="card">
                                <div class="card-body hstack gap-3 align-items-start">
                                    <i class="bi bi-exclamation-circle fs-5 text-dark"></i>
                                    <div class="vstack gap-1">
                                        <span class="text-secondary">Cancellation</span>
                                        @if (data.event.cancellationChargeAmount) {
                                            <span class="text-dark">
                                                {{ null | currencyUnit : data.event.cancellationChargeAmount : data.venue.currencyCode }}
                                                cancellation charge per person
                                            </span>
                                        }
                                        @if (data.event.cancellationChargePartyAmount) {
                                            <span class="text-dark">
                                                {{ null | currencyUnit : data.event.cancellationChargePartyAmount : data.venue.currencyCode }}
                                                cancellation charge per booking
                                            </span>
                                        }
                                    </div>
                                </div>
                            </div>
                        }

                        @if (data.slotGroups && data.slotGroups.length > 0) {
                            <div class="card">
                                <div class="card-body hstack gap-3 align-items-start">
                                    <i class="bi bi-clock fs-5 text-dark"></i>
                                    <div class="vstack gap-1">
                                        <span class="text-secondary">Date &amp; Time</span>
                                        @for (slotGroup of data.slotGroups; track slotGroup.start.getTime()) {
                                            <span class="text-dark">
                                                {{ slotGroup.start | date:'EEEE d MMMM, h:mm a' }}
                                                @if (slotGroup.slotCount > 1) {
                                                    -
                                                    {{ slotGroup.end | date:(slotGroup.start.toDateString() === slotGroup.end.toDateString() ? 'h:mm a' : 'EEEE d MMMM, h:mm a') }}
                                                }
                                            </span>
                                        }
                                        <ng-container
                                            *ngTemplateOutlet="otherEventDates; context: {
                                                eventDateOptions: data.eventDateOptions,
                                                activeDateTime: data.activeDateTime
                                            }"
                                        >
                                        </ng-container>
                                    </div>
                                </div>
                            </div>
                        } @else if (data.activeDateTime) {
                            <div class="card">
                                <div class="card-body hstack gap-3 align-items-start">
                                    <i class="bi bi-clock fs-5 text-dark"></i>
                                    <div class="vstack gap-1">
                                        <span class="text-secondary">Date &amp; Time</span>
                                        <span class="text-dark">{{ data.activeDateTime | date:'EEEE d MMMM, h:mm a' }}</span>
                                        <ng-container
                                            *ngTemplateOutlet="otherEventDates; context: {
                                                eventDateOptions: data.eventDateOptions,
                                                activeDateTime: data.activeDateTime
                                            }"
                                        >
                                        </ng-container>
                                    </div>
                                </div>
                            </div>
                        } @else if (data.event.recurrence.startDates.length > 0) {
                            <div class="card">
                                <div class="card-body hstack gap-3 align-items-start">
                                    <i class="bi bi-clock fs-5 text-dark"></i>
                                    <div class="vstack gap-1">
                                        <span class="text-secondary">Times</span>
                                        @for (startDate of data.event.recurrence.startDates; track startDate) {
                                            <span class="text-dark">{{ startDate | date:'EEEE d MMMM, h:mm a' }}</span>
                                        }
                                    </div>
                                </div>
                            </div>
                        }

                        <div class="card">
                            <div class="card-body hstack gap-3 align-items-start">
                                <i class="bi bi-geo-alt fs-5 text-dark"></i>
                                <div class="vstack gap-1">
                                    <span class="text-secondary">Venue</span>
                                    <span class="text-dark" style="white-space: pre-line">{{ data.venue.address.lines() }}</span>
                                </div>
                            </div>
                        </div>

                        @if (data.venue.phoneNumber) {
                            <div class="card">
                                <div class="card-body hstack gap-3 align-items-start">
                                    <i class="bi bi-telephone fs-5 text-dark"></i>
                                    <div class="vstack gap-1">
                                        <span class="text-secondary">Phone</span>
                                        <a [href]="'tel:' + data.venue.phoneNumber" class="text-dark text-decoration-none">{{ data.venue.phoneNumber }}</a>
                                    </div>
                                </div>
                            </div>
                        }

                        @if (data.mapUrl) {
                            <div class="vstack gap-2">
                                <h6 class="fw-semibold m-0">Location</h6>
                                <iframe
                                    class="w-100 rounded"
                                    height="300"
                                    style="border:0"
                                    loading="lazy"
                                    allowfullscreen
                                    referrerpolicy="no-referrer-when-downgrade"
                                    [src]="data.mapUrl | safeURL"
                                >
                                </iframe>
                                @if (data.directionsUrl) {
                                    <a
                                        [href]="data.directionsUrl"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        class="btn btn-outline-secondary btn-sm w-100"
                                    >
                                        <i class="bi bi-geo-alt me-1"></i>
                                        Open in Google Maps
                                    </a>
                                }
                            </div>
                        }
                    </div>
                </div>
            </div>

            <p class="text-muted text-center mt-4 small">
                Powered by
                <a
                    class="link-primary"
                    [href]="marketingUrl"
                    target="_blank"
                >
                    TableSense
                </a>
            </p>
        </div>
    }
</div>

<ng-template
    #otherEventDates
    let-eventDateOptions="eventDateOptions"
    let-activeDateTime="activeDateTime"
>
    @if (eventDateOptions && eventDateOptions.length > 1 && activeDateTime) {
        <hr class="my-2 border-dark-subtle" />
        <div class="vstack gap-1">
            <label
                class="form-label text-secondary mb-0"
                for="event-date-select"
            >
                Other dates available
            </label>
            <select
                id="event-date-select"
                class="form-select event-date-select"
                aria-label="Choose event date"
                [ngModel]="dateTimeSelectValue(activeDateTime)"
                (ngModelChange)="dateTimeSelected($event)"
            >
                @for (eventDateTime of eventDateOptions; track dateTimeSelectValue(eventDateTime)) {
                    <option [value]="dateTimeSelectValue(eventDateTime)">
                        {{ eventDateTime | date:'EEEE d MMMM' }}
                    </option>
                }
            </select>
        </div>
    }
</ng-template>
